import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';

/* Loads and keeps fresh all primary case data (case, documents, timeline,
 * contradictions, generic chat history, AI usage). Also polls while any
 * document is in the "processing" state and refreshes once done. */
export function useCaseData(caseId) {
  const [caseData, setCaseData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [contradictions, setContradictions] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [aiUsage, setAiUsage] = useState({ total_tokens: 0, cost_usd: 0 });
  const [analysisProgress, setAnalysisProgress] = useState({ active: false });
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        api.get(`/cases/${caseId}`),
        api.get(`/cases/${caseId}/documents`),
        api.get(`/cases/${caseId}/timeline`),
        api.get(`/cases/${caseId}/contradictions`),
        api.get(`/cases/${caseId}/chat`),
        api.get(`/cases/${caseId}/ai-usage`),
      ]);
      if (results[0].status === 'fulfilled') setCaseData(results[0].value.data.case);
      if (results[1].status === 'fulfilled') setDocuments(results[1].value.data.documents);
      if (results[2].status === 'fulfilled') setTimeline(results[2].value.data.timeline);
      if (results[3].status === 'fulfilled') setContradictions(results[3].value.data.contradictions);
      if (results[4].status === 'fulfilled') setChatMessages(results[4].value.data.messages);
      if (results[5].status === 'fulfilled') setAiUsage(results[5].value.data);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll while analysis/re-evaluation is running.
  // During analysis we poll ONLY the lightweight /analysis-progress endpoint (the overlay
  // needs nothing more). We deliberately DO NOT re-fetch the full /documents list every cycle
  // — for large cases that payload is heavy and, combined with the in-flight AI load, caused
  // the origin to time out (520). We do one full refresh when analysis completes.
  // Recursive setTimeout (not setInterval) prevents request pile-up if the server is slow.
  useEffect(() => {
    const hasProcessing = documents.some(d => d.analysis_status === 'processing');
    if (!hasProcessing && !analysisProgress.active) return;
    let cancelled = false;
    let timer;

    const refreshAll = async () => {
      const results = await Promise.allSettled([
        api.get(`/cases/${caseId}/documents`),
        api.get(`/cases/${caseId}`),
        api.get(`/cases/${caseId}/timeline`),
        api.get(`/cases/${caseId}/contradictions`),
      ]);
      if (cancelled) return;
      if (results[0].status === 'fulfilled') setDocuments(results[0].value.data.documents);
      if (results[1].status === 'fulfilled') setCaseData(results[1].value.data.case);
      if (results[2].status === 'fulfilled') setTimeline(results[2].value.data.timeline);
      if (results[3].status === 'fulfilled') setContradictions(results[3].value.data.contradictions);
    };

    const poll = async () => {
      try {
        const { data: prog } = await api.get(`/cases/${caseId}/analysis-progress`);
        if (cancelled) return;
        setAnalysisProgress(prog);
        if (!prog.active) {
          await refreshAll();
          return; // stop; effect re-runs if anything is still processing
        }
      } catch (err) {
        logger.error('Progress poll failed:', err);
      }
      if (!cancelled) timer = setTimeout(poll, 3000);
    };

    timer = setTimeout(poll, 3000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [documents, caseId, analysisProgress.active]);

  return { caseData, documents, timeline, contradictions, chatMessages, setChatMessages, aiUsage, analysisProgress, setAnalysisProgress, loading, loadAll };
}
